import styled from 'styled-components';
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  Select,
  SelectProps,
  FormControl,
  DialogTitle as MUIDialogTitle,
  DialogActions as MUIDialogActions,
  MenuItem,
  ListSubheader as MUIListSubheader,
  Paper,
} from '@mui/material';
import React from 'react';
import { ColorDot } from 'spw-components/worksheet/styles';
import { PaperComponentProps } from 'spw-components/dialogs/total-program/types';
import { colors } from 'spw-theme';
import { JustifyStartRow } from 'spw-shared/globalComponents';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { withStyles } from 'tss-react/mui';
import { CSSObject } from 'tss-react/types';

export const DialogLabel = styled.label`
  && {
    margin-right: 16px;
    font-size: 14px;
  }

  &&.ml-16 {
    margin-left: 16px;
  }
`;

export const SpaceCategoryColorDot = styled(ColorDot)`
  && {
    width: 32px;
    height: 32px;
    border-radius: 16px;
  }
`;

const spaceTypeRootStyles = {
  boxSizing: 'border-box',
  background: 'white',
  borderRadius: '8px !important',
  height: '56px',
  width: '338px',
} as CSSObject;

const spaceTypeSelectStyles = {
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'start',
} as CSSObject;

export const SpaceTypeSelect = withStyles(
  (props: SelectProps) => {
    return (
      <FormControl>
        <Select
          className="reassign-modal-select"
          IconComponent={ExpandMore}
          MenuProps={{
            style: {
              maxHeight: 320,
              width: '299px',
              paddingBottom: 10,
            },
            MenuListProps: {
              style: { paddingTop: '0' },
            },
            disableScrollLock: true,
          }}
          {...props}
        />
      </FormControl>
    );
  },
  {
    root: spaceTypeRootStyles,
    select: spaceTypeSelectStyles,
  }
);

export const SpaceCategorySelect = withStyles(
  (props: SelectProps) => {
    return (
      <Select
        className="reassign-modal-select"
        IconComponent={ExpandMore}
        MenuProps={{
          disableScrollLock: true,
        }}
        {...props}
      />
    );
  },
  {
    root: {
      ...spaceTypeRootStyles,
    },
    select: spaceTypeSelectStyles,
  }
);

export const Spacer8 = styled.div`
  && {
    height: 8px;
    overflow: hidden;
  }
`;

export const ReassignSpaceTypesDialogContainer = withStyles(
  (props: DialogProps & { PaperProps?: Partial<PaperComponentProps> }) => (
    <Dialog {...props} disableScrollLock />
  ),
  {
    root: {
      '& .MuiPaper-root': {
        width: '843px',
        maxWidth: '843px',
      },
    },
  }
);

export const ReassignSpaceTypeDialogContent = styled(DialogContent)`
  && {
    padding-top: 8px;
    p {
      margin-top: 0;
      font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

export const SpaceTypesSelectContainer = styled(Box)`
  && {
    display: flex;
    margin-top: 24px;
  }
`;

export const DialogTitle = styled(MUIDialogTitle)`
  && {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
  }
`;

export const DialogActions = styled(MUIDialogActions)`
  && {
    justify-content: flex-start;
    margin-top: 40px;
    padding-left: 0;
  }
`;

export const MenuItemRow = styled(MenuItem)`
  && {
    display: flex;
    justify-content: flex-start;
    height: 24px;
    margin-bottom: 12px;
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
  }

  &&.first {
    margin-top: 12px;
  }
`;

export const ListSubheader = styled(MUIListSubheader)`
  && {
    color: ${colors.black};
    font-family: Roboto;
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 16px;

    margin-bottom: 16px;

    padding-top: 8px;
    border-top: 1px solid #e0e0e0;

    text-transform: uppercase;
  }
`;

export const EmptyValueBox = styled.div`
  && {
    font-size: 14px;
    line-height: 24px;
  }
`;

export const ValueRow = styled(JustifyStartRow)`
  && {
    font-size: 14px;
  }
`;

export const ClientsOrInternalNamesTitle = styled.h4`
  margin: 0px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;
  margin-right: 24px;
`;

export const InternalSpaceTypeName = styled.div`
  .value {
    color: ${colors.black};
    font-family: Roboto;
    font-size: 16px;
    line-height: 20px;
  }

  .label {
    font-size: 14px;
    color: ${colors.mainGray};
  }
`;

export const ClientSpaceNameDialogCard = styled(Paper)`
  width: 400px;

  .title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;

    padding: 24px 24px 4px;
  }

  .content {
    padding: 20px 24px 12px;
  }

  .checkbox-label {
    margin: 24px 0;

    .MuiCheckbox-root {
      margin-right: 7px;
    }
  }

  .error-message {
    margin: 4px 0px 8px;
    font-size: 14px;
    line-height: 20px;
  }

  .info {
    display: flex;
    align-items: flex-start;

    .info-text {
      margin-left: 8px;
      font-size: 14px;
      line-height: 20px;
      strong {
        font-weight: 500;
      }
    }
  }
`;

export const ClientNamesStatusContainer = styled.div`
  text-transform: uppercase;
  color: ${colors.black};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
`;

export const ClientNamesStatus = ({
  clientFacingNamesAreVisible,
}: {
  clientFacingNamesAreVisible: boolean;
}) => (
  <ClientNamesStatusContainer>
    {clientFacingNamesAreVisible ? 'Client Names' : 'Internal Names'}
  </ClientNamesStatusContainer>
);
